import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  StateBackend,
  StateBackendTestResult,
  AwsKubernetesAccessTestResult,
  Integration,
  IntegrationStatus,
  SyncRun,
} from '@lizrd/contracts';
import {
  disconnectIntegration,
  getIntegrationHealth,
  refreshBilling,
  setStateBackend,
  testStateBackend,
  setKubernetesAccess,
  testKubernetesAccess,
  triggerIntegrationSync,
  validateIntegration,
} from '../lib/api';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { useAuthStore } from '../store/auth';
import { PROVIDER_ACCENT, integrationIdentifier } from '../lib/graph-visuals';
import { ProviderLogo } from './ProviderLogo';
import { Lock, FolderOpen, Hexagon } from 'lucide-react';
import { RegionsScanned } from './RegionsScanned';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert } from './ui/alert';
import { Input } from './ui/input';
import { Toggle } from './ui/toggle';
import { ConfirmByTypingModal } from './ui/confirm-by-typing-modal';
import { AddIntegrationModal } from './AddIntegrationModal';

const STATUS_VARIANT: Record<
  IntegrationStatus,
  'success' | 'info' | 'warning' | 'danger' | 'neutral'
> = {
  CONNECTED: 'success',
  SYNCING: 'info',
  WARNING: 'warning',
  ERROR: 'danger',
  PENDING: 'info', // optimistically connected — verifying access
  DISCONNECTED: 'neutral',
};
/** Human labels where the raw status isn't self-explanatory. */
const STATUS_LABEL: Partial<Record<IntegrationStatus, string>> = {
  PENDING: 'verifying',
};
const RUN_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  SUCCEEDED: 'success',
  RUNNING: 'info',
  QUEUED: 'neutral',
  FAILED: 'danger',
};
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

/** Provider-specific "how to fix / what to expect" billing guidance. */
function billingGuidance(provider: string, status: 'pending' | 'error' | 'not_configured'): string {
  if (status === 'pending') {
    // Configured, but no data yet — reassure + tell them roughly how long.
    if (provider === 'GCP') {
      return 'Your BigQuery billing export is set, but the table hasn’t produced data yet. GCP creates and backfills it within a few hours of enabling the export — no action needed. If it’s still pending after ~24 h, double-check that the "Standard usage cost" export is enabled and pointed at the dataset you configured, and that the table name matches.';
    }
    if (provider === 'AWS') {
      return 'Cost Explorer is still activating for this account — AWS can take up to ~24 h to make billed cost available on a newly connected account. No action needed; it’ll clear automatically.';
    }
    return 'Billed cost isn’t available for this account yet — it’s still becoming available and should clear automatically within a day.';
  }
  if (status === 'not_configured') {
    if (provider === 'GCP') {
      return 'GCP only exposes billed cost through a BigQuery billing export. In the Cloud Console enable Billing → Billing export → BigQuery, grant the Lizrd viewer service account BigQuery Data Viewer on that dataset, then add the export table (project.dataset.table) to this integration (or re-run the setup script).';
    }
    return 'Connect a billing export so Lizrd can read this account’s billed cost.';
  }
  // status === 'error'
  switch (provider) {
    case 'AWS':
      return 'Verify the Lizrd role still allows Cost Explorer reads (ce:GetCostAndUsage) and that Cost Explorer is enabled on the account, then re-run the connect to refresh access.';
    case 'GCP':
      return 'Verify the BigQuery billing-export table still exists and that the Lizrd viewer service account keeps BigQuery Data Viewer on its dataset, then reconnect.';
    case 'AZURE':
      return 'Verify the Lizrd app still has the Cost Management Reader role on the subscription, then reconnect.';
    default:
      return 'Reconnect the account to restore billed-cost access.';
  }
}

/** Detail + actions for one integration (opened from the data-flow graph). */
export function IntegrationDetailModal({
  envId,
  integration,
  canManage,
  onClose,
}: {
  envId: string;
  integration: Integration;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  // Type-to-confirm disconnect (replaces a native window.confirm).
  const [confirming, setConfirming] = useState(false);
  // In-place GitLab token update (re-keys the same connection — no delete+recreate).
  const [updatingToken, setUpdatingToken] = useState(false);
  const accent = PROVIDER_ACCENT[integration.provider] ?? '#8b93a7';
  const isGitLab = integration.provider === 'GITLAB';

  // Poll health from the live server state (not the frozen prop snapshot): fast
  // while a run is in flight, slower to keep the last-synced time and the manual
  // cooldown countdown fresh, and stop once idle with no cooldown pending.
  const health = useQuery({
    queryKey: ['integration-health', integration.id],
    queryFn: () => getIntegrationHealth(envId, integration.id),
    refetchInterval: (q) => {
      const d = q.state.data;
      const active = d
        ? d.status === 'SYNCING' ||
          d.recentRuns.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING')
        : integration.status === 'SYNCING';
      if (active) return 2000;
      const until = d?.manualSyncAvailableAt ? new Date(d.manualSyncAvailableAt).getTime() : 0;
      return until > Date.now() ? 15000 : false;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['integrations', envId] });
    qc.invalidateQueries({ queryKey: ['integration-health', integration.id] });
  };

  const sync = useMutation({
    mutationFn: () => triggerIntegrationSync(envId, integration.id),
    onSuccess: () => {
      setFeedback(null);
      invalidate();
    },
    onError: (e) =>
      setFeedback({ ok: false, message: e instanceof Error ? e.message : 'Failed to start sync' }),
  });
  const validate = useMutation({
    mutationFn: () => validateIntegration(envId, integration.id),
    onSuccess: (r) => {
      setFeedback({
        ok: r.ok,
        message: r.message ?? (r.ok ? 'Connection OK' : 'Validation failed'),
      });
      invalidate();
    },
    onError: (e) =>
      setFeedback({ ok: false, message: e instanceof Error ? e.message : 'Validation failed' }),
  });
  const remove = useMutation({
    mutationFn: () => disconnectIntegration(envId, integration.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e) => {
      setConfirming(false);
      setFeedback({ ok: false, message: e instanceof Error ? e.message : 'Failed to disconnect' });
    },
  });
  // On-demand billed-cost refresh (e.g. right after enabling a GCP export) so the
  // pending/issue flag can clear without waiting for the periodic retry. Org-level, so
  // it's admin-gated (matching the /billing/refresh endpoint).
  const role = useAuthStore((s) => s.user?.role);
  const canRecheckBilling = role ? roleHasPermissions(role, [Permission.OrgManage]) : false;
  const recheckBilling = useMutation({
    mutationFn: () => refreshBilling(),
    onSuccess: () => invalidate(),
  });

  const status = health.data?.status ?? integration.status;
  const lastSyncedAt = health.data?.lastSyncedAt ?? integration.lastSyncedAt;
  const lastError = health.data?.lastError ?? integration.lastError;
  const runs: SyncRun[] = health.data?.recentRuns ?? [];
  const runActive =
    status === 'SYNCING' || runs.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING');
  const cooldownMs = health.data?.manualSyncAvailableAt
    ? new Date(health.data.manualSyncAvailableAt).getTime() - Date.now()
    : 0;
  const cooldownActive = !runActive && cooldownMs > 0;
  const cooldownMins = Math.max(1, Math.ceil(cooldownMs / 60_000));

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={
          <div className="flex items-center gap-2">
            <ProviderLogo provider={integration.provider} size={24} />
            <div className="min-w-0">
              <h3 className="truncate font-display text-base font-bold text-bone">
                {integration.displayName}
              </h3>
              <p className="text-[0.7rem] text-muted">
                {integration.provider}
                {integrationIdentifier(integration) && (
                  <span className="font-mono"> · {integrationIdentifier(integration)}</span>
                )}
              </p>
            </div>
            <Badge variant={STATUS_VARIANT[status]} dot>
              {STATUS_LABEL[status] ?? status.toLowerCase()}
            </Badge>
          </div>
        }
        footer={
          canManage ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => sync.mutate()}
                disabled={sync.isPending || runActive || cooldownActive}
                title={
                  cooldownActive ? 'Manual sync is limited to once every 15 minutes' : undefined
                }
              >
                {runActive
                  ? 'Syncing…'
                  : cooldownActive
                    ? `↻ Sync in ~${cooldownMins}m`
                    : '↻ Sync now'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => validate.mutate()}
                disabled={validate.isPending}
              >
                Validate
              </Button>
              {isGitLab && (
                <Button size="sm" variant="outline" onClick={() => setUpdatingToken(true)}>
                  Update token
                </Button>
              )}
              <button
                onClick={() => setConfirming(true)}
                disabled={remove.isPending}
                className="ml-auto text-xs font-semibold text-danger hover:underline disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          ) : null
        }
      >
        <div className="space-y-4">
          <div
            className="rounded-md border p-3 text-sm"
            style={{
              borderColor: `color-mix(in srgb, ${accent} 35%, transparent)`,
              background: `color-mix(in srgb, ${accent} 6%, transparent)`,
            }}
          >
            <div className="flex justify-between text-xs">
              <span className="text-muted">Last synced</span>
              <span className="text-bone">{when(lastSyncedAt)}</span>
            </div>
            {health.data?.regions && (
              <RegionsScanned variant="full" regions={health.data.regions} className="mt-2" />
            )}
            {status === 'PENDING' ? (
              <p className="mt-1 text-xs text-iris">
                ⏳ Verifying access — cloud IAM changes can take a few minutes to propagate.
                We&rsquo;ll confirm automatically on the next sync.
              </p>
            ) : (
              lastError && <p className="mt-1 text-xs text-danger">⚠ {lastError}</p>
            )}
            {cooldownActive && (
              <p className="mt-1 text-xs text-muted">
                ⏳ Manual sync is available again in ~{cooldownMins} min.
              </p>
            )}
            <p className="mt-1.5 border-t border-hairline pt-1.5 text-[0.7rem] text-muted">
              <Lock aria-hidden className="mr-1 inline-block h-3 w-3 align-text-bottom" />
              Read-only. Lizrd stores no {integration.kind === 'SCM' ? 'repo' : 'cloud'} secret —
              access uses short-lived tokens you can revoke anytime.
            </p>
          </div>

          {feedback && (
            <Alert variant={feedback.ok ? 'success' : 'danger'}>{feedback.message}</Alert>
          )}

          {/* Billing-read health — distinct from sync. A source can flow fine yet still
              fail to yield billed cost. `pending` (configured, still populating) is
              informational; `error`/`not_configured` need action. */}
          {(integration.billingStatus === 'pending' ||
            integration.billingStatus === 'error' ||
            integration.billingStatus === 'not_configured') && (
            <Alert variant={integration.billingStatus === 'pending' ? 'info' : 'warning'}>
              <div className="space-y-1 text-xs">
                <p className="font-semibold text-bone">
                  {integration.billingStatus === 'pending'
                    ? 'Billed cost is on its way'
                    : integration.billingStatus === 'not_configured'
                      ? 'Billed cost isn’t set up for this account'
                      : 'We can’t read this account’s billed cost'}
                </p>
                <p>{billingGuidance(integration.provider, integration.billingStatus)}</p>
                <p className="text-muted">
                  {integration.billingStatus === 'pending'
                    ? 'Until it arrives, this account’s cost shows as estimates. We keep re-checking automatically.'
                    : 'Until this is fixed, this account’s cost shows as estimates only.'}
                </p>
                {canRecheckBilling && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-1"
                    disabled={recheckBilling.isPending}
                    onClick={() => recheckBilling.mutate()}
                  >
                    {recheckBilling.isPending ? 'Re-checking…' : 'Re-check billing now'}
                  </Button>
                )}
                {recheckBilling.isSuccess && (
                  <span className="ml-2 text-muted">Re-checked — refresh to see the latest.</span>
                )}
              </div>
            </Alert>
          )}

          {(integration.provider === 'AWS' ||
            integration.provider === 'GCP' ||
            integration.provider === 'AZURE') &&
            canManage && <TerraformStateSection integration={integration} />}

          {(integration.provider === 'AWS' || integration.provider === 'GCP') && canManage && (
            <KubernetesAccessSection integration={integration} />
          )}

          <div>
            <div className="mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
              Recent sync runs
            </div>
            {health.isLoading && <p className="text-sm text-muted">Loading…</p>}
            {!health.isLoading && runs.length === 0 && (
              <p className="text-sm text-muted">No sync runs yet.</p>
            )}
            <ul className="space-y-2">
              {runs.map((run) => (
                <li key={run.id} className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={RUN_VARIANT[run.status] ?? 'neutral'}>
                      {run.status.toLowerCase()}
                    </Badge>
                    <span className="text-muted">{run.trigger.toLowerCase()}</span>
                    {run.status === 'RUNNING' && (
                      <span className="text-xs text-muted">
                        {run.stage ?? '…'} {run.progressPct}%
                      </span>
                    )}
                    {run.status === 'SUCCEEDED' && run.stats != null && (
                      <span className="text-xs text-muted">
                        {String((run.stats as { discovered?: number }).discovered ?? 0)} discovered
                        {(() => {
                          const n = (run.stats as { regions?: { scanned?: string[] } }).regions
                            ?.scanned?.length;
                          return n ? ` · ${n} region${n === 1 ? '' : 's'}` : '';
                        })()}
                      </span>
                    )}
                    {run.error && <span className="text-xs text-danger">{run.error}</span>}
                  </div>
                  <span className="flex-none text-[0.7rem] text-muted">{when(run.createdAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
      <ConfirmByTypingModal
        open={confirming}
        title="Disconnect this integration?"
        description={
          <>
            This removes <strong className="text-bone">{integration.displayName}</strong> and its
            stored access from this domain. Its resources drop out of the graph on the next sync —
            you can reconnect later.
          </>
        }
        confirmPhrase={integration.displayName}
        confirmLabel="Disconnect"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirming(false)}
      />
      {updatingToken && (
        <AddIntegrationModal
          envId={envId}
          provider="GITLAB"
          mode="update"
          initialBaseUrl={
            (integration.config?.baseUrl as string | undefined) ?? 'https://gitlab.com'
          }
          initialGroupPath={(integration.config?.groupPath as string | undefined) ?? ''}
          onClose={() => setUpdatingToken(false)}
          onConnected={() => {
            setUpdatingToken(false);
            setFeedback({
              ok: true,
              message:
                'Token updated. If Reviews is on and the token can post, the merge-request webhook is registered automatically.',
            });
            invalidate();
          }}
        />
      )}
    </>
  );
}

/** Per-provider string fields for the Terraform-state form. AWS adds a KMS toggle
 * (handled separately below); the three shapes match the `StateBackend` union. */
const STATE_FIELDS: Record<
  'AWS' | 'GCP' | 'AZURE',
  { name: string; label: string; placeholder: string }[]
> = {
  AWS: [
    { name: 'bucket', label: 'State S3 bucket', placeholder: 'acme-tfstate-123456789012' },
    { name: 'key', label: 'State key / path', placeholder: 'prod/terraform.tfstate' },
  ],
  GCP: [
    { name: 'bucket', label: 'State GCS bucket', placeholder: 'acme-tfstate' },
    { name: 'object', label: 'State object path', placeholder: 'prod/default.tfstate' },
  ],
  AZURE: [
    { name: 'storageAccount', label: 'Storage account', placeholder: 'acmetfstate' },
    { name: 'container', label: 'Blob container', placeholder: 'tfstate' },
    { name: 'key', label: 'Blob key / path', placeholder: 'prod.terraform.tfstate' },
  ],
};

/** The state store per provider (AWS=S3, GCP=GCS, Azure=azurerm blob), for the blurb. */
const STATE_STORE: Record<'AWS' | 'GCP' | 'AZURE', string> = {
  AWS: 'S3',
  GCP: 'GCS',
  AZURE: 'Azure Blob Storage',
};

/**
 * Connect Terraform state (TD-058 Phase B): the customer points Lizrd at their
 * `terraform.tfstate` so cloud↔code correlation is exact (confidence 1.0), shown as
 * "verified from state" badges. Works for all three clouds — AWS (S3, optional
 * SSE-KMS), GCP (GCS), and Azure (azurerm blob) — via the provider-tagged
 * StateBackend union. The AWS KMS key ARN is required when the state is SSE-KMS
 * (least privilege — we grant `kms:Decrypt` on that key only).
 */
function TerraformStateSection({ integration }: { integration: Integration }) {
  const envId = integration.domainId;
  const provider = integration.provider as 'AWS' | 'GCP' | 'AZURE';
  const fields = STATE_FIELDS[provider];
  const qc = useQueryClient();
  const current = (integration.config?.stateBackend ?? null) as Record<string, string> | null;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, current?.[f.name] ?? ''])),
  );
  const [kms, setKms] = useState(!!current?.kmsKeyArn);
  const [kmsArn, setKmsArn] = useState(current?.kmsKeyArn ?? '');
  const [test, setTest] = useState<StateBackendTestResult | null>(null);

  const setValue = (name: string, v: string) => setValues((s) => ({ ...s, [name]: v }));
  const filled = fields.every((f) => values[f.name]?.trim());
  const valid = filled && (provider !== 'AWS' || !kms || !!kmsArn.trim());

  const payload = (): StateBackend => {
    const t = (name: string) => (values[name] ?? '').trim();
    if (provider === 'GCP') return { provider: 'GCP', bucket: t('bucket'), object: t('object') };
    if (provider === 'AZURE')
      return {
        provider: 'AZURE',
        storageAccount: t('storageAccount'),
        container: t('container'),
        key: t('key'),
      };
    return {
      provider: 'AWS',
      bucket: t('bucket'),
      key: t('key'),
      ...(kms && kmsArn.trim() ? { kmsKeyArn: kmsArn.trim() } : {}),
    };
  };
  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations', envId] });

  const testMut = useMutation({
    mutationFn: () => testStateBackend(envId, integration.id, payload()),
    onSuccess: (r) => setTest(r),
  });
  const saveMut = useMutation({
    mutationFn: () => setStateBackend(envId, integration.id, payload()),
    onSuccess: invalidate,
  });
  const clearMut = useMutation({
    mutationFn: () => setStateBackend(envId, integration.id, null),
    onSuccess: () => {
      invalidate();
      setValues(Object.fromEntries(fields.map((f) => [f.name, ''])));
      setKms(false);
      setKmsArn('');
      setTest(null);
    },
  });
  const busy = testMut.isPending || saveMut.isPending || clearMut.isPending;

  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
      <div className="mb-1 flex items-center gap-2">
        <FolderOpen aria-hidden className="h-4 w-4 shrink-0 text-muted" />
        <span className="text-sm font-semibold text-bone">Terraform state (optional)</span>
        {current && (
          <Badge variant="success" dot>
            connected
          </Badge>
        )}
      </div>
      <p className="mb-3 text-xs text-muted">
        Point Lizrd at your <code>terraform.tfstate</code> in {STATE_STORE[provider]} so we match
        code to cloud resources exactly — “verified from state,” not guessed. Read-only; the state
        is never stored.
      </p>

      <div className="space-y-2">
        {fields.map((f) => (
          <label
            key={f.name}
            className="block text-[0.7rem] font-semibold uppercase tracking-wide text-muted"
          >
            {f.label}
            <Input
              className="mt-1"
              value={values[f.name] ?? ''}
              onChange={(e) => setValue(f.name, e.target.value)}
              placeholder={f.placeholder}
            />
          </label>
        ))}
        {provider === 'AWS' && (
          <>
            <div className="flex items-center gap-2 pt-1">
              <Toggle checked={kms} onChange={setKms} label="State is KMS-encrypted" />
              <span className="text-xs text-haze">My state is KMS-encrypted (SSE-KMS)</span>
            </div>
            {kms && (
              <label className="block text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                KMS key ARN (required)
                <Input
                  className="mt-1"
                  value={kmsArn}
                  onChange={(e) => setKmsArn(e.target.value)}
                  placeholder="arn:aws:kms:us-east-1:…:key/…"
                />
              </label>
            )}
          </>
        )}
      </div>

      {test && (
        <Alert className="mt-3" variant={test.ok ? 'success' : 'danger'}>
          {test.ok ? (
            <>✓ Read {test.resourceCount ?? 0} resources from state.</>
          ) : (
            <div className="space-y-1">
              <div>{test.message ?? 'Could not read the state.'}</div>
              {test.setupUrl && (
                <a
                  href={test.setupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-iris underline"
                >
                  Update your IAM role ↗
                </a>
              )}
            </div>
          )}
        </Alert>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!valid || busy}
          onClick={() => testMut.mutate()}
        >
          {testMut.isPending ? 'Testing…' : 'Test connection'}
        </Button>
        <Button size="sm" disabled={!valid || busy} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </Button>
        {current && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => clearMut.mutate()}
            className="ml-auto text-muted"
          >
            Disconnect state
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Kubernetes (EKS/GKE) pod-level discovery. Read-only and keyless — the cluster is
 * read with a short-lived token; no secret is stored. By default Lizrd sets up the
 * read-only cluster grant for you (auto-provision): on AWS it manages its own EKS
 * Access Entry + AWS-managed view policy; on GCP the viewer SA's `roles/container.viewer`
 * (from the setup script) maps to k8s read. Opt out to apply a tightly-scoped grant
 * yourself — we show the exact snippets, and discovery only reads once it's in place.
 */
function KubernetesAccessSection({ integration }: { integration: Integration }) {
  const envId = integration.domainId;
  const isGcp = integration.provider === 'GCP';
  const platform = isGcp ? 'GKE' : 'EKS';
  const qc = useQueryClient();
  const stored = (integration.config?.kubernetesAccess ?? null) as {
    enabled?: boolean;
    autoProvision?: boolean;
  } | null;
  // Track locally so the UI flips immediately on success (no modal refetch needed).
  const [enabled, setEnabled] = useState(Boolean(stored?.enabled));
  const [autoProvision, setAutoProvision] = useState(stored?.autoProvision !== false); // default on
  const [test, setTest] = useState<AwsKubernetesAccessTestResult | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations', envId] });
  const onErr = (e: unknown) =>
    setMsg({ ok: false, text: e instanceof Error ? e.message : 'Something went wrong.' });

  const testMut = useMutation({
    mutationFn: () => testKubernetesAccess(envId, integration.id),
    onSuccess: (r) => {
      setTest(r);
      setMsg(null);
    },
    onError: onErr,
  });
  const enableMut = useMutation({
    mutationFn: () => setKubernetesAccess(envId, integration.id, { enabled: true, autoProvision }),
    onSuccess: () => {
      setEnabled(true);
      setMsg({
        ok: true,
        text: autoProvision
          ? `Enabled. Lizrd will set up read-only ${platform} access on the next sync, then discover workloads.`
          : `Enabled. Apply the grant below, then run a sync to discover workloads.`,
      });
      invalidate();
    },
    onError: onErr,
  });
  const saveModeMut = useMutation({
    mutationFn: () => setKubernetesAccess(envId, integration.id, { enabled: true, autoProvision }),
    onSuccess: () => {
      setMsg({ ok: true, text: 'Saved.' });
      invalidate();
    },
    onError: onErr,
  });
  const disableMut = useMutation({
    mutationFn: () => setKubernetesAccess(envId, integration.id, null),
    onSuccess: () => {
      setEnabled(false);
      setTest(null);
      setMsg({ ok: true, text: 'Kubernetes discovery disabled.' });
      invalidate();
    },
    onError: onErr,
  });
  const busy =
    testMut.isPending || enableMut.isPending || disableMut.isPending || saveModeMut.isPending;

  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
      <div className="mb-1 flex items-center gap-2">
        <Hexagon aria-hidden className="h-4 w-4 shrink-0 text-muted" />
        <span className="text-sm font-semibold text-bone">Kubernetes workloads (optional)</span>
        {enabled && (
          <Badge variant="success" dot>
            enabled
          </Badge>
        )}
      </div>
      <p className="mb-3 text-xs text-muted">
        Discover {platform} Deployments, StatefulSets, and DaemonSets and right-size their
        CPU/memory requests. <span className="text-bone">Read-only and keyless</span> — the cluster
        is read with a short-lived token, and no cluster secret is ever stored.
      </p>

      {/* Auto vs manual grant. */}
      <div className="mb-3 rounded-md border border-hairline bg-surface/60 p-2.5">
        <div className="flex items-center gap-2">
          <Toggle
            checked={autoProvision}
            onChange={setAutoProvision}
            label="Auto-provision access"
          />
          <span className="text-xs font-semibold text-bone">Set up access automatically</span>
          <Badge variant={autoProvision ? 'success' : 'neutral'}>
            {autoProvision ? 'recommended' : 'manual'}
          </Badge>
        </div>
        {autoProvision ? (
          <p className="mt-2 text-[0.7rem] text-muted">
            {isGcp ? (
              <>
                Uses the viewer service account’s <code>roles/container.viewer</code> grant
                (included in your GCP setup script), which GKE maps to read-only Kubernetes access —
                nothing else to do.
              </>
            ) : (
              <>
                Lizrd manages <span className="text-bone">only its own</span> EKS Access Entry and
                attaches the AWS-managed read-only view policy (excludes Secrets). No{' '}
                <code>kubectl</code> step, and it self-heals if the entry ever goes stale.
              </>
            )}
          </p>
        ) : (
          <ManualGrantSnippet integration={integration} />
        )}
      </div>

      {msg && (
        <Alert className="mb-3" variant={msg.ok ? 'success' : 'danger'}>
          {msg.text}
        </Alert>
      )}

      {test && (
        <Alert className="mb-3" variant={test.ok ? 'success' : 'danger'}>
          {test.ok ? (
            <>
              ✓ Read {test.workloadCount ?? 0} workload(s) across {test.clusterCount ?? 0}{' '}
              cluster(s).
            </>
          ) : (
            <div className="space-y-1">
              <div>{test.message ?? 'Could not read the Kubernetes API.'}</div>
              {test.unauthorized && autoProvision && (
                <div className="text-[0.7rem]">
                  Auto-provision is on — press <span className="font-semibold">Test access</span>{' '}
                  again to re-create the grant and retry.
                </div>
              )}
              {test.setupUrl && (
                <a
                  href={test.setupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-iris underline"
                >
                  Update your IAM role ↗
                </a>
              )}
            </div>
          )}
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => testMut.mutate()}>
          {testMut.isPending ? 'Testing…' : 'Test access'}
        </Button>
        {!enabled ? (
          <Button size="sm" disabled={busy} onClick={() => enableMut.mutate()}>
            {enableMut.isPending ? 'Enabling…' : 'Enable'}
          </Button>
        ) : (
          <>
            {/* Let an enabled integration switch its grant mode without re-enabling. */}
            {autoProvision !== (stored?.autoProvision !== false) && (
              <Button size="sm" disabled={busy} onClick={() => saveModeMut.mutate()}>
                {saveModeMut.isPending ? 'Saving…' : 'Save change'}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => disableMut.mutate()}
              className="ml-auto text-muted"
            >
              Disable
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** The exact, copy-paste grant to run when auto-provision is off. Read-only and
 * tightly scoped; discovery only reads once it's applied. */
function ManualGrantSnippet({ integration }: { integration: Integration }) {
  const isGcp = integration.provider === 'GCP';
  const cfg = (integration.config ?? {}) as {
    roleArn?: string;
    serviceAccountEmail?: string;
    projectId?: string;
  };
  const snippet = isGcp
    ? `# Grant the viewer service account read-only Kubernetes access\n` +
      `# (GKE maps roles/container.viewer to read-only k8s RBAC):\n` +
      `gcloud projects add-iam-policy-binding ${cfg.projectId ?? '<PROJECT_ID>'} \\\n` +
      `  --member="serviceAccount:${cfg.serviceAccountEmail ?? '<VIEWER_SA_EMAIL>'}" \\\n` +
      `  --role="roles/container.viewer" --condition=None\n` +
      `# Or, for a tighter grant, bind the SA email to a read-only ClusterRole via a\n` +
      `# ClusterRoleBinding (subject kind: User, name: the SA email).`
    : `# 1) Map Lizrd's role to a read-only group via an EKS Access Entry (per cluster):\n` +
      `aws eks create-access-entry --cluster-name <YOUR_CLUSTER> \\\n` +
      `  --principal-arn ${cfg.roleArn ?? '<LIZRD_ROLE_ARN>'} \\\n` +
      `  --kubernetes-groups lizrd-readonly\n` +
      `# 2) Apply the scoped, read-only ClusterRole + binding (get/list only; no Secrets):\n` +
      `kubectl apply -f infrastructure/kubernetes/lizrd-readonly-clusterrole.yaml`;
  return (
    <div className="mt-2 space-y-1.5">
      <p className="text-[0.7rem] text-muted">
        Apply this read-only grant yourself — discovery only reads once it’s in place:
      </p>
      <pre className="max-h-48 overflow-auto rounded-sm border border-hairline bg-surface p-2 font-mono text-[0.65rem] leading-relaxed text-haze">
        {snippet}
      </pre>
    </div>
  );
}
